import Model from 'src/storage/list';
import moment from 'moment';
import config from 'src/config';
import constants from 'src/constants';

const apiHost = config.apiHost();
const apiPath = apiHost + '/api/0/json';
const defaultCurrency = constants.general.defaultCurrency;

class InvoiceModel extends Model {
  id = 'InvoiceModel';
  persistent = false;
  refreshOnInit = false;
  refreshOnSubscribe = true;
  sortBy = null;
  defaultState = () => ({
    current: 'recent',
    pageIndex: 0,
    date: moment(),
    sortBy: {
      sortKey: 'invoiceDate',
      desc: true
    },
    ...this.defaultAddInvoice()
  });
  defaultAddInvoice = () => ({
    invoiceDate: moment(),
    patientId: 0,
    currency: this.getCurrency(),
    invoiceDetails: [
      {
        description: '',
        price: '',
        vat: '',
        currency: this.getCurrency()
      }
    ]
  });
  apiConfig = {
    refresh: {
      url: 'invoice/refresh',
      validation: {
        skip: 'skip',
        limit: 'limit'
      },
      cancellable: true,
      event: 'invoice_list'
    },
    list: {
      url: 'invoice/list',
      validation: {
        skip: 'skip',
        limit: 'limit'
      },
      cancellable: true,
      event: 'invoice_list'
    },
    search: {
      url: 'invoice/list',
      validation: {
        skip: 'skip',
        limit: 'limit'
      },
      cancellable: true,
      event: 'invoice_list'
    },
    create: {
      url: 'invoice/create',
      validation: {
        invoiceNo: 'invoiceNo',
        invoiceDate: 'invoiceDate',
        patientId: 'patientId',
        invoiceDetails: 'invoiceDetails',
        serie: 'serie'
      },
      cancellable: true,
      event: 'invoice_create'
    },
    detail: {
      url: 'invoice/detail',
      validation: {
        id: 'baseId'
      },
      cancellable: true
    },
    pdfLink: {
      url: 'invoice/pdfLink',
      validation: {
        id: 'baseId'
      }
    },
    pay: {
      url: 'invoice/pay',
      validation: {
        id: 'baseId',
        cardId: 'baseId'
      },
      cancellable: true,
      event: 'invoice_pay'
    },
    refund: {
      url: 'invoice/refund',
      validation: {
        id: 'baseId'
      },
      cancellable: true,
      event: 'invoice_refund'
    },
    clinic: {
      url: 'account/detail',
      validation: {
        id: 'baseId'
      },
      cancellable: true
    },
    createPaymentIntent: {
      url: 'invoice/createPaymentIntent',
      cancellable: true,
      validation: {
        id: 'baseId'
      }
    },
    confirmPaymentIntent: {
      url: 'invoice/confirmPaymentIntent',
      cancellable: true,
      validation: {
        paymentIntentId: 'string'
      }
    }
  };

  _events = {
    profile: 'handleProfile',
    'foxsocket:message:invoice': 'handleInvoiceWS',
    invoice: 'handleInvoice'
  };

  pay = (data, cb) => {
    this.api('pay', { ...data }, (err, data, success) => {
      cb && cb(err, data, success);
      if (success) {
        let invoice = data?.invoice;
        this.setListState({
          items: this.items().map((item) =>
            invoice.id === item.id ? { ...item, ...invoice } : item
          )
        });
        this.refresh();
      }
    });
  };
  createPaymentIntent = (id, cb) => {
    this.setStateSilent({ invoiceId: id, paymentIntent: null, clientSecret: null });
    return this.api('createPaymentIntent', { id }, (err, res, success) => {
      if (success) {
        this.setState({ paymentIntent: res, clientSecret: res.clientSecret });
      }
      cb && cb(err, res, success);
    });
  };

  confirmPaymentIntent = (paymentIntent, cb) => {
    return this.api(
      'confirmPaymentIntent',
      { paymentIntentId: paymentIntent.id },
      (err, res, success) => {
        // this.emit('payment_result', { paymentIntent, err, data: res, success });
        if (success) this.setStateSilent({ confirmPaymentResponse: res });
        typeof cb == 'function' && cb(err, res, success);
      }
    );
  };

  handleProfile = (profile) => {
    this.user = profile;
    this.setState({ user: profile });
  };

  handleInvoiceWS = ({ data }) => {
    this.log('handleInvoiceWS', data);
    this.handleInvoice(data);
  };

  handleInvoice = (invoice) => {
    this.log('handleInvoice', invoice);
    this.addItem(invoice);
  };

  listParams() {
    let req = {
      skip: this.state.pageIndex * this.listLimit,
      limit: this.listLimit,
      invoiceDate: 0,
      sortBy: this.state.sortBy,
      doctorIds: this.state.user.doctorIds
    };
    let date = this.input('searchDate');
    if (date) {
      req.invoiceDate = date.format('YYYYMMDD');
    }
    let query = this.listInput('search');
    if (query.length > 0) {
      req.query = query;
    }
    return req;
  }

  create = (cb) => {
    let { patientId, invoiceNo, invoiceDate, invoiceDetails, serie } = this.state;
    let req = {
      invoiceNo,
      serie,
      invoiceDate: parseInt(invoiceDate.format('YYYYMMDD')),
      patientId,
      invoiceDetails: invoiceDetails.map((item) => ({
        ...item,
        vat: parseFloat(item.vat),
        price: parseFloat(item.price)
      })),
      currency: this.getCurrency()
    };
    this.api('create', req, (err, data, success) => {
      cb && cb(err, data, success);
      if (success) {
        this.resetAddInvoice();
        this.refresh();
      }
    });
  };

  refund = (data, cb) => {
    let req = {
      id: data.id
    };
    this.api('refund', req, (err, data, success) => {
      cb && cb(err, data, success);
      if (success) {
        this.setListState({
          items: this.items().map((item) => (data.id === item.id ? { ...item, ...data } : item))
        });
        this.refresh();
      }
    });
  };

  // The PDF needs a login. Ask the server for a short-lived link, then open it.
  // The tab is opened inside the click so popup blockers allow it.
  openPdf = (item) => {
    const w = window.open('', '_blank');
    if (w) w.opener = null;
    this.api('pdfLink', { id: item.id }, (err, data, success) => {
      if (success && data?.ok && data.token) {
        const url =
          apiPath + '/invoice/pdf?id=' + encodeURIComponent(item.id) + '&dl=' + encodeURIComponent(data.token);
        if (w) w.location.href = url;
        else window.location.href = url;
      } else if (w) {
        w.close();
      }
    });
  };

  loadDetail = (id, cb) => {
    let req = {
      id
    };
    this.api('detail', req, (err, data, success) => {
      cb && cb(err, data, success);
    });
  };

  initSerie = async () => {
    this.setStateSilent({ serie: '', invoiceNo: null });
    let { isDoctor, clinicIds, invoice } = this.state.user || {};
    if (!isDoctor) return;
    const all = [
      new Promise((resolve) =>
        this.api(
          'refresh',
          {
            skip: 0,
            limit: 1,
            invoiceDate: 0
          },
          (err, data, success) => {
            if (success && data.total > 0) {
              resolve(data.items[0]);
            } else {
              resolve(null);
            }
          }
        )
      )
    ];

    if (clinicIds?.length) {
      all.push(
        new Promise((resolve) =>
          this.api(
            'clinic',
            {
              id: clinicIds[0]
            },
            (err, data, success) => {
              if (success && data?.invoice) {
                resolve(data.invoice);
              } else {
                resolve(null);
              }
            }
          )
        )
      );
    }

    let [previous, clinicInvoice] = await Promise.all(all);

    let serie = clinicInvoice?.serie || previous?.serie || invoice?.serie || 'LIFEAPP';
    let invoiceNo = previous?.invoiceNo + 1 || 1;
    this.setState({ serie, invoiceNo });
  };

  resetAddInvoice() {
    this.initSerie();
    this.resetApiState('create');
    this.setState(this.defaultAddInvoice());
  }

  addInvoiceDetail = () => {
    this.setState({
      invoiceDetails: [
        ...this.state.invoiceDetails,
        {
          description: '',
          price: '',
          vat: '',
          currency: this.getCurrency()
        }
      ]
    });
  };

  delInvoiceDetail = (delIds) => {
    const details = this.input('invoiceDetails');
    this.handleChange('invoiceDetails', [...details.filter((item, i) => delIds.indexOf(i) === -1)]);
  };

  getCurrency = () => {
    return this.input('currency') || this.getUser()?.currency || defaultCurrency;
  };
}

const model = new InvoiceModel();
export default model;
